import React, { useState } from "react";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  Grid,
  Card,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Tooltip,
  Box,
  TextField,
  Popper,
  Paper,
  ClickAwayListener,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import ArticleIcon from "@mui/icons-material/Article";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";

const PAGE_SIZE = 9;

function PublicationsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pageIndex, setPageIndex] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [publications, setPublications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // appliedFilters only change on Search / Reset — not while user picks months
  const [appliedFilters, setAppliedFilters] = useState({ fromMonth: null, toMonth: null });

  // Month range filter (same pattern as ClaimsList)
  const [filters, setFilters] = useState({ fromMonth: null, toMonth: null });
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMonthPicker, setOpenMonthPicker] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPub, setDialogPub] = useState(null);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Fetch current user
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({ queryKey: ["me"], queryFn: me, retry: false });
  const isAdmin = meData?.role_id === 1 || meData?.role_id === 2;

  // Fetch publications from server with pagination + month range filter
  const fetchPublications = async (page = 0, fromMonth = null, toMonth = null) => {
    if (!meData) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: PAGE_SIZE,
        from_month: fromMonth ? fromMonth.format("YYYY-MM") : "",
        to_month: toMonth ? toMonth.format("YYYY-MM") : "",
      });
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/publications/all?${params.toString()}`,
        { withCredentials: true }
      );
      setPublications(response.data.data || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to fetch publications.", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Re-fetch when page changes — uses appliedFilters only
  React.useEffect(() => {
    if (meData) fetchPublications(pageIndex, appliedFilters.fromMonth, appliedFilters.toMonth);
  }, [meData, pageIndex, appliedFilters]);

  // Handle default selection from navigation state (Notification click)
  React.useEffect(() => {
    if (location.state?.field_id && publications.length > 0) {
      const pubId = Number(location.state.field_id);
      const matchedPub = publications.find((p) => Number(p.id) === pubId);
      if (matchedPub) {
        openDialog(matchedPub);
        // Clear state to avoid re-opening if the user navigates back and forth
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, publications]);

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    return dayjs(monthStr + "-01").format("MMMM YYYY");
  };

  const handleSearch = () => {
    const newFilters = { fromMonth: filters.fromMonth, toMonth: filters.toMonth };
    setAppliedFilters(newFilters);
    setPageIndex(0);
    setOpenMonthPicker(false);
  };
  const handleReset = () => {
    setFilters({ fromMonth: null, toMonth: null });
    setAppliedFilters({ fromMonth: null, toMonth: null });
    setPageIndex(0);
    setOpenMonthPicker(false);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));


  const openDialog = (pub) => {
    setDialogPub(pub);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setDialogPub(null);
  };

  const totalLinks = (pub) =>
    (pub.newsletter_links?.length || 0) +
    (pub.magazine_links?.length || 0) +
    (pub.other_links?.length || 0);

  // --- Sub-components ---
  const SectionLinks = ({ title, icon: Icon, links, accentColor }) => {
    if (!links || links.length === 0) return null;
    return (
      <MDBox mb={2}>
        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
          <Icon sx={{ color: accentColor, fontSize: 18 }} />
          <MDTypography variant="caption" fontWeight="bold" sx={{ color: accentColor, textTransform: "uppercase", fontSize: "0.72rem" }}>
            {title}
          </MDTypography>
          <Chip label={links.length} size="small" sx={{ ml: "auto", backgroundColor: `${accentColor}22`, color: accentColor, height: 18, fontSize: "0.65rem" }} />
        </MDBox>
        {links.map((url, idx) => (
          <Box
            key={idx}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 1,
              mb: 0.5,
              borderRadius: 1,
              border: `1px solid ${accentColor}22`,
              backgroundColor: `${accentColor}07`,
              "&:hover": { backgroundColor: `${accentColor}15` },
            }}
          >
            <MDTypography
              variant="caption"
              sx={{ fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, color: "#333" }}
            >
              Link {idx + 1}: {url}
            </MDTypography>
            <Tooltip title="Open in new tab">
              <IconButton size="small" onClick={() => window.open(url, "_blank")} sx={{ color: accentColor, ml: 1, flexShrink: 0 }}>
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </MDBox>
    );
  };

  SectionLinks.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    links: PropTypes.arrayOf(PropTypes.string),
    accentColor: PropTypes.string.isRequired,
  };

  return (
    <DashboardLayout>
      <MDBox mt={3} />

      {/* Filter Card */}
      <Card sx={{ mb: 2 }}>
        <MDBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between">
          <MDBox>
            <MDTypography variant="h5" fontWeight="medium">Publications</MDTypography>
            <MDTypography variant="caption" color="text">Month-wise publications — Newsletters, Magazines & More</MDTypography>
          </MDBox>
          {isAdmin && (
            <MDButton
              variant="gradient"
              color="info"
              startIcon={<AddCircleIcon />}
              onClick={() => navigate("/publications/add")}
              sx={{ borderRadius: 2, ml: "auto" }}
            >
              Add Publication
            </MDButton>
          )}
        </MDBox>

        <MDBox px={2} pt={1.5} pb={1}>
          <Grid container spacing={3} alignItems="flex-end">
            {/* Month Range — same component pattern as ClaimsList */}
            <Grid item xs={12} md={4}>
              <MDTypography style={{ fontSize: "16px" }} color="dark">Month Range</MDTypography>
              <TextField
                fullWidth
                size="small"
                value={
                  filters.fromMonth && filters.toMonth
                    ? `${filters.fromMonth.format("MMM YYYY")} - ${filters.toMonth.format("MMM YYYY")}`
                    : filters.fromMonth
                      ? `From ${filters.fromMonth.format("MMM YYYY")}`
                      : ""
                }
                placeholder="Select Month Range"
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setOpenMonthPicker((prev) => !prev);
                }}
                InputProps={{
                  readOnly: true,
                  sx: { cursor: "pointer", backgroundColor: "#fff", height: "45px" },
                }}
              />
              <Popper open={openMonthPicker} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={() => setOpenMonthPicker(false)}>
                  <Paper elevation={3} sx={{ p: 2, mt: 1, display: "flex", gap: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="From Month"
                        views={["year", "month"]}
                        value={filters.fromMonth}
                        onChange={(val) => setFilters((f) => ({ ...f, fromMonth: val }))}
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            InputProps: { readOnly: true, sx: { fontSize: "14px", height: "45px" } },
                          },
                        }}
                      />
                      <DatePicker
                        label="To Month"
                        views={["year", "month"]}
                        value={filters.toMonth}
                        onChange={(val) => setFilters((f) => ({ ...f, toMonth: val }))}
                        maxDate={dayjs()}
                        minDate={filters.fromMonth || undefined}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            InputProps: { readOnly: true, sx: { fontSize: "14px", height: "45px" } },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox p={2}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                onClick={handleSearch}
                sx={{
                  color: "white !important",
                  backgroundColor: "#1976d2",
                  "&:hover": { backgroundColor: "#155fa0" },
                  minWidth: "120px",
                }}
              >
                Search
              </MDButton>
            </Grid>
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                variant="outlined"
                onClick={handleReset}
                sx={{
                  color: "#b71c1c !important",
                  borderColor: "#b71c1c",
                  "&:hover": { backgroundColor: "#fbe9e7", borderColor: "#b71c1c" },
                  minWidth: "120px",
                }}
              >
                Cancel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      {/* Cards Grid */}
      <MDBox px={3} py={2}>
        {isLoading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress color="info" />
          </MDBox>
        ) : publications.length === 0 ? (
          <Card>
            <MDBox p={6} textAlign="center">
              <MDTypography variant="h5" color="text" fontWeight="medium" mb={1}>No Publications Found</MDTypography>
              <MDTypography variant="body2" color="text">
                {isAdmin ? "Click \"Add Publication\" to add the first one." : "No publications available."}
              </MDTypography>
            </MDBox>
          </Card>
        ) : (
          <>
            <Grid
              container
              spacing={4}
              alignItems="stretch"
            >
              {publications.map((pub) => {
                const total = totalLinks(pub);
                const hasNewsletter = pub.newsletter_links?.length > 0;
                const hasMagazine = pub.magazine_links?.length > 0;
                const hasOther = pub.other_links?.length > 0;

                return (
                  <Grid
                    item
                    size={{ xs: 12, sm: 6, md: 4 }}
                    key={pub.id}
                    sx={{ display: "flex" }}
                  >
                    <Card
                      sx={{
                        width: "100%",
                        minWidth: "100%",
                        height: "250px", // Fixed Height
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                        border: "1px solid #f0f0f0",
                        overflow: "hidden",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      {/* Card Header — fixed 64px */}
                      <Box
                        sx={{
                          background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                          height: "64px",
                          px: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexShrink: 0,
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <CalendarMonthIcon sx={{ color: "#fff", fontSize: 20 }} />
                          <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#fff", fontSize: "0.95rem" }}>
                            {formatMonth(pub.month)}
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={0.5}>
                          <Chip
                            label={`${total} link${total !== 1 ? "s" : ""}`}
                            size="small"
                            sx={{ backgroundColor: "rgba(255,255,255,0.25)", color: "#fff", fontWeight: "bold", fontSize: "0.7rem" }}
                          />
                          {isAdmin && (
                            <Tooltip title="Edit Publication">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigate(`/publications/edit/${pub.id}--${formatMonth(pub.month)}`)
                                }
                                sx={{ color: "#fff" }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </MDBox>
                      </Box>

                      {/* Category Badges — fixed 48px */}
                      <MDBox px={2} display="flex" gap={0.8} flexWrap="wrap" alignItems="center"
                        sx={{ height: "48px", borderBottom: "1px solid #f5f5f5", overflow: "hidden", flexShrink: 0 }}>
                        {hasNewsletter && (
                          <Chip icon={<ArticleIcon sx={{ fontSize: "12px !important" }} />} label={`Newsletter (${pub.newsletter_links.length})`} size="small" sx={{ backgroundColor: "#e3f2fd", color: "#1976d2", fontSize: "0.68rem" }} />
                        )}
                        {hasMagazine && (
                          <Chip icon={<MenuBookIcon sx={{ fontSize: "12px !important" }} />} label={`Magazines (${pub.magazine_links.length})`} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontSize: "0.68rem" }} />
                        )}
                        {hasOther && (
                          <Chip icon={<FolderOpenIcon sx={{ fontSize: "12px !important" }} />} label={`Others (${pub.other_links.length})`} size="small" sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontSize: "0.68rem" }} />
                        )}
                        {total === 0 && (
                          <MDTypography variant="caption" color="text" sx={{ fontSize: "0.75rem" }}>No links added.</MDTypography>
                        )}
                      </MDBox>

                      {/* Card Body — fills remaining space, with minHeight to ensure stability */}
                      <Box sx={{ flexGrow: 1, px: 2, py: 1.5, display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden" }}>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", fontSize: "0.85rem", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                          {total > 0
                            ? `${total} publication link${total !== 1 ? "s" : ""} across ${[hasNewsletter, hasMagazine, hasOther].filter(Boolean).length} sections.`
                            : "No publication links for this month."}
                        </MDTypography>

                        {total > 0 && (
                          <MDButton
                            variant="outlined"
                            color="info"
                            startIcon={<LinkIcon />}
                            onClick={() => openDialog(pub)}
                            fullWidth
                            sx={{ mt: "auto", borderRadius: 2, fontSize: "0.78rem" }}
                          >
                            View Links
                          </MDButton>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Pagination — same as TicketList */}
            <Box display="flex" justifyContent="center" width="100%" mt={3}>
              <Pagination
                count={totalPages}
                page={pageIndex + 1}
                onChange={(e, value) => setPageIndex(value - 1)}
                color="primary"
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
                siblingCount={0}
                boundaryCount={1}
              />
            </Box>
          </>
        )}
      </MDBox>

      {/* Links Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <CalendarMonthIcon sx={{ color: "#1976d2", fontSize: 22 }} />
            <MDTypography variant="h6" fontWeight="bold">
              {dialogPub ? formatMonth(dialogPub.month) : ""} — Publications
            </MDTypography>
          </MDBox>
          <IconButton onClick={closeDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ m: 0 }} />
        <DialogContent sx={{ pt: 2 }}>
          {dialogPub && (
            <>
              <SectionLinks title="Newsletter Publications" icon={ArticleIcon} links={dialogPub.newsletter_links} accentColor="#1976d2" />
              <SectionLinks title="Magazines Publications" icon={MenuBookIcon} links={dialogPub.magazine_links} accentColor="#2e7d32" />
              <SectionLinks title="Other Publications" icon={FolderOpenIcon} links={dialogPub.other_links} accentColor="#e65100" />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} variant="outlined" color="inherit" size="small">Close</Button>
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%", color: "white !important" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default PublicationsList;
